<script setup lang="ts">
import AssigneeLink from '@/components/AssigneeLink.vue';
import Chip from '@/components/Chip.vue';
import DotText from '@/components/DotText.vue';
import Link from '@/components/Link.vue';
import MarkdownText from '@/components/MarkdownText.vue';
import Note from '@/components/Note.vue';
import Dialog from '@/components/dialogs/Dialog.vue';
import CreateNote from '@/components/forms/CreateNoteForm.vue';
import { useAuthStore } from '@/stores/auth';
import { useFileStore } from '@/stores/file';
import { useModuleStore } from '@/stores/module';
import { useOrganizationStore } from '@/stores/organization';
import { type DetailedActionPoint, type NoteCreatedByFullName } from '@/types/actionPoint';
import type { ActionPointFile } from '@/types/files';
import type { Note as NoteType } from '@/types/note';
import type { Organization } from '@/types/organization';
import { getActionPointStatusItem, getOrganizationIdFromActionPoint } from '@/utils/actionPoint';
import { isDateBefore, toDisplayDate } from '@/utils/date';
import { determineFileType, fileToDataUrl } from '@/utils/file';
import { storeToRefs } from 'pinia';
import { computed, defineModel, defineProps, ref, watch } from 'vue';

const authStore = useAuthStore();
const moduleStore = useModuleStore();
const organizationStore = useOrganizationStore();
const fileStore = useFileStore();

const isDialogOpen = defineModel<boolean>();

const props = defineProps<{
    actionPoint: DetailedActionPoint | null;
}>();

const files = ref<ActionPointFile[]>([]);
const actionPointOrganization = ref<Organization | null>();

const actionPoint = computed(() => props.actionPoint);

const { isActionPointExecutorFeatureEnabled } = storeToRefs(authStore);

const filesByNoteId = computed(() => {
    const map = new Map<string, ActionPointFile[]>();
    for (const file of files.value) {
        if (!map.has(file.noteId)) map.set(file.noteId, []);

        map.get(file.noteId)!.push(file);
    }
    return map;
});

const statusItem = computed(() => (actionPoint.value ? getActionPointStatusItem(actionPoint.value?.status) : null));

watch(
    () => isDialogOpen.value,
    async (isOpen) => {
        if (!props.actionPoint || !isOpen) return;

        const orgId = getOrganizationIdFromActionPoint(props.actionPoint);
        files.value = await fileStore.getActionPointFiles(orgId, props.actionPoint._id);

        if (props.actionPoint.organization._id != authStore.currentOrganization?._id) {
            actionPointOrganization.value = await organizationStore.fetchById(orgId);
        }
    },
    { immediate: true }
);

async function noteCreated(note: NoteType, attachedFiles: File[]) {
    actionPoint.value?.notes?.push(note as NoteCreatedByFullName);

    const newFiles = await Promise.all(
        attachedFiles.map(async (file) => {
            const dataUrl = await fileToDataUrl(file);

            return {
                noteId: note._id as string,
                name: file.name,
                contentType: file.type,
                type: determineFileType(file.type),
                data: dataUrl as string
            };
        })
    );

    files.value.push(...newFiles);
}
</script>

<template>
    <Dialog v-model="isDialogOpen" :title="actionPoint?.action ? `Actiepunt: ${actionPoint.action}` : ''">
        <v-row v-if="!actionPoint" class="justify-center align-center">
            <v-fade-transition appear>
                <v-progress-circular color="primary" size="69" width="8" indeterminate />
            </v-fade-transition>
        </v-row>

        <v-fade-transition v-else>
            <div>
                <h3>Algemene informatie</h3>
                <v-row>
                    <v-col lg="6">
                        <div class="d-flex flex-column">
                            <div class="field-holder">
                                <span class="font-weight-bold">Actie</span>
                                <span>{{ actionPoint.action }}</span>
                            </div>

                            <div class="field-holder">
                                <span class="font-weight-bold">Beschrijving</span>
                                <MarkdownText
                                    v-if="actionPoint.description"
                                    :markdown="actionPoint.description"
                                    class="bg-grey-lighten-4 text-black pa-2"
                                />
                                <span v-else>-</span>
                            </div>

                            <div class="field-holder">
                                <span class="font-weight-bold">Verantwoordelijk</span>
                                <span class="d-flex align-center ga-1">
                                    <AssigneeLink v-if="actionPoint.responsible?._id" :detailedAssignee="actionPoint.responsible" />
                                    <span v-else>-</span>
                                </span>
                            </div>

                            <div v-if="isActionPointExecutorFeatureEnabled" class="field-holder">
                                <span class="font-weight-bold">Uitvoerder</span>
                                <span class="d-flex align-center ga-1">
                                    <AssigneeLink v-if="actionPoint.executor?._id" :detailedAssignee="actionPoint.executor" />
                                    <span v-else>-</span>
                                </span>
                            </div>
                        </div>
                    </v-col>

                    <v-col lg="6">
                        <div class="field-holder">
                            <span class="font-weight-bold">Risiconiveau</span>
                            <DotText :value="actionPoint.risk?.description || ''" :color="actionPoint.risk?.color || 'red'" />
                        </div>

                        <div class="field-holder">
                            <span class="font-weight-bold">Prioriteit</span>
                            <DotText :value="actionPoint.priority?.name || ''" :color="actionPoint.priority?.color || 'red'" />
                        </div>

                        <div class="field-holder" v-if="moduleStore.areMultipleModulesAccessible">
                            <span class="font-weight-bold">Module</span>
                            <span class="d-flex align-center">
                                <Link
                                    v-if="actionPoint.module"
                                    :route-to="{ name: 'module', params: { module: actionPoint.module?.name } }"
                                    icon="grid_view"
                                    :displayText="actionPoint.module?.name"
                                />
                                <span v-else>-</span>
                            </span>
                        </div>

                        <div class="field-holder">
                            <span class="font-weight-bold">Status</span>
                            <span class="d-flex align-center">
                                <Chip :color="statusItem?.color" :text="statusItem?.title" />
                            </span>
                        </div>
                        <div class="field-holder" v-if="actionPoint.organization._id != authStore.currentOrganization?._id">
                            <span class="font-weight-bold">Bedrijf</span>
                            <span class="d-flex align-center">
                                <Link
                                    :route-to="{
                                        name: 'organization-profile',
                                        params: { organizationId: actionPoint.organization._id }
                                    }"
                                    icon="business"
                                    :displayText="actionPointOrganization?.name || ''"
                                />
                            </span>
                        </div>

                        <div class="field-holder">
                            <span class="font-weight-bold">Deadline</span>
                            <span
                                class="d-flex align-center ga-2"
                                v-if="actionPoint.deadline"
                                :class="{ 'text-red': isDateBefore(actionPoint.deadline) }"
                            >
                                <v-icon icon="calendar_today" />
                                <span :class="{ 'font-weight-bold': isDateBefore(actionPoint.deadline) }"
                                    >{{ toDisplayDate(actionPoint.deadline, true) }}
                                </span>
                            </span>
                            <span v-else>-</span>
                        </div>

                        <div class="field-holder">
                            <span class="font-weight-bold">Bron</span>
                            <span class="d-flex align-center ga-2">
                                <template v-if="actionPoint.source != undefined"> <v-icon icon="flag" />{{ actionPoint.source.name }} </template>
                                <span v-else>-</span>
                            </span>
                        </div>
                    </v-col>
                </v-row>

                <v-divider class="mb-4" />
                <h3>Mutaties</h3>
                <v-row>
                    <v-col lg="6">
                        <div class="d-flex flex-column">
                            <div class="field-holder">
                                <span class="font-weight-bold">Aangemaakt op</span>
                                <span class="d-flex align-center ga-2">
                                    <v-icon icon="calendar_today" />
                                    <span v-if="actionPoint.createdOn"> {{ toDisplayDate(actionPoint.createdOn, true) }} </span>
                                </span>
                            </div>
                            <div class="field-holder">
                                <span class="font-weight-bold">Aangemaakt door</span>
                                <span class="d-flex align-center ga-2" v-if="actionPoint.createdBy">
                                    <Link
                                        :route-to="{ name: 'employee', params: { id: actionPoint.createdBy._id } }"
                                        icon="person"
                                        :displayText="actionPoint.createdBy.fullName"
                                    />
                                </span>
                                <span v-else>-</span>
                            </div>

                            <div class="field-holder">
                                <span class="font-weight-bold">Afgerond op</span>
                                <span class="d-flex align-center ga-2" v-if="actionPoint.completedOn">
                                    <v-icon icon="calendar_today" />
                                    <span>{{ toDisplayDate(actionPoint.completedOn, true) }} </span>
                                </span>
                                <span v-else>-</span>
                            </div>
                        </div>
                    </v-col>

                    <v-col lg="6">
                        <div class="field-holder">
                            <span class="font-weight-bold">Laatst bijgewerkt op</span>
                            <span class="d-flex align-center ga-1" v-if="actionPoint.updatedOn">
                                <v-icon icon="calendar_today" />
                                <span> {{ toDisplayDate(actionPoint.updatedOn, true) }} </span>
                            </span>
                            <span v-else>-</span>
                        </div>

                        <div class="field-holder">
                            <span class="font-weight-bold">Laatst bijgewerkt door</span>
                            <span class="d-flex align-center" v-if="actionPoint.updatedBy">
                                <Link
                                    :route-to="{ name: 'employee', params: { id: actionPoint.updatedBy._id } }"
                                    icon="person"
                                    :displayText="actionPoint.updatedBy.fullName"
                                />
                            </span>
                            <span v-else>-</span>
                        </div>
                        <div class="field-holder">
                            <span class="font-weight-bold">Laatste wijziging</span>
                            <span class="d-flex align-center ga-1">
                                <template v-if="actionPoint.updatedMessage">
                                    <v-icon icon="update" />
                                    <span> {{ actionPoint.updatedMessage || '-' }} </span>
                                </template>
                                <span v-else>-</span>
                            </span>
                        </div>
                    </v-col>
                </v-row>
                <v-divider class="mb-4" />
                <h3>Notities</h3>
                <v-row class="flex-column">
                    <v-col>
                        <span v-if="!actionPoint.notes || actionPoint.notes.length === 0" class="mb-8">Dit actiepunt heeft nog geen notities</span>
                        <template v-else>
                            <div v-for="note in actionPoint.notes" :key="note._id" class="mb-5">
                                <Note :note="note" :actionPointId="actionPoint._id" :files="filesByNoteId.get(note._id!) || []" />
                            </div>
                        </template>

                        <template v-if="!authStore.isReadOnly()">
                            <h4 class="mt-10">Notitie toevoegen</h4>
                            <CreateNote :actionPointId="actionPoint._id" @note-created="noteCreated" />
                        </template>
                    </v-col>
                </v-row>
            </div>
        </v-fade-transition>
    </Dialog>
</template>

<style scoped>
.field-holder {
    display: flex;
    flex-direction: column;
    margin-bottom: 20px;
    white-space: pre-wrap;
}
</style>
